import type { userAgent } from 'next/server';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import CarouselDisplayTrigger from '@alltrails/amplitude/enums/CarouselDisplayTrigger';
import { useExperiments } from '@alltrails/experiments';
import type DeeplinkData from '@alltrails/types/DeeplinkData';
import { InstallPromptType } from '../types';
import getAnalyticsParams from '../utils/getAnalyticsParams';
import useBaseBranchUrl from './useBaseBranchUrl';

const branchLinkFallback = 'https://alltrails.io/rgGVELw1wCb';

type BranchDeepLinkParams = {
  deeplinkData?: DeeplinkData;
  customAppPath?: string;
  displayTrigger?: CarouselDisplayTrigger;
  isEnabled: boolean;
  promptType?: InstallPromptType;
};

const useBranchDeeplink = ({ isEnabled, deeplinkData, customAppPath, promptType, displayTrigger }: BranchDeepLinkParams) => {
  const experiments = useExperiments();
  const browserName = useSelector((state: { browser?: ReturnType<typeof userAgent>['browser'] }) => state.browser?.name);
  const [deeplink, setDeeplink] = useState(branchLinkFallback);
  const baseBranchUrl = useBaseBranchUrl();

  useEffect(() => {
    if (!isEnabled) {
      return;
    }

    let appPath = 'alltrails://screen/explore';

    if (customAppPath) {
      appPath = `alltrails://${customAppPath}`;
    } else if (deeplinkData) {
      const { pageType, pageId } = deeplinkData;

      if (pageType === 'navigate') {
        appPath = `alltrails://screen/recorder?trailID=${pageId}&start=false`;
      } else {
        appPath = `alltrails://${pageType}/${pageId}`;
      }
    }

    const analyticsParams = getAnalyticsParams({ promptType, browserName, displayTrigger, experiments });
    const search = analyticsParams.toString();
    const params = search ? `&${search}` : '';

    // Branch long-link isn't built using URLParams to prevent serializing deeplink paths.
    const branchDeeplink = `${baseBranchUrl}&$ios_deeplink_path=${appPath}&$android_deeplink_path=${appPath}${params}`;
    setDeeplink(branchDeeplink);
  }, [promptType, displayTrigger, experiments, browserName, baseBranchUrl, isEnabled, deeplinkData, customAppPath]);

  return deeplink;
};

export default useBranchDeeplink;
